import React, { useState, useEffect } from 'react';
import { Controller, Control, FieldErrors, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { collaboratorSchemaHelpers, PcdType } from '@/features/Collaborators/Models/collaborator';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { CollaboratorFormData } from '@/features/Collaborators/Edit/Types';
import { FileUploadInput } from '@/components/shared';
import { useFileUpload } from '@/hooks/form-handlers/useFileUpload';

interface SpecialNeedsFieldsetProps {
    context: CollaboratorFormContext;
    collaboratorId: string;
    control: Control<CollaboratorFormData>;
    setValue: UseFormSetValue<CollaboratorFormData>;
    watch: UseFormWatch<CollaboratorFormData>;
    errors: FieldErrors<CollaboratorFormData>;
    isSubmitting?: boolean;
}

const pcdOptions: PcdType[] = ['Física', 'Auditiva', 'Visual', 'Mental', 'Intelectual'];

const SpecialNeedsFieldset: React.FC<SpecialNeedsFieldsetProps> = ({
    context,
    collaboratorId,
    control,
    setValue,
    watch,
    errors,
    isSubmitting = false
}) => {
    // Helper para verificar se campo deve aparecer
    const shouldShow = (fieldName: string) => collaboratorSchemaHelpers.shouldShowField(fieldName, context);

    // Estado local para os dados do formulário (necessário para o useFileUpload)
    const [formData, setFormData] = useState<CollaboratorFormData>({} as CollaboratorFormData);

    // Hook de upload de arquivos
    const { handleFileChange, uploadStates } = useFileUpload(setFormData);

    // Sincronizar mudanças do React Hook Form com o estado local
    useEffect(() => {
        const subscription = watch((value) => {
            setFormData(value as CollaboratorFormData);
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    // Helper local para obter layout contextual
    const getLayoutByContext = (context: CollaboratorFormContext) => {
        switch (context) {
            case 'DP_UPDATE':
            case 'DHO_UPDATE':
                return {
                    fieldsetStyle: 'border p-4 rounded-md',
                    legendStyle: 'text-sm font-medium px-1 text-gray-400'
                };
            default: // FULL_FORM
                return {
                    fieldsetStyle: 'border p-4 rounded-md',
                    legendStyle: 'text-sm font-medium px-1 text-gray-400'
                };
        }
    };

    const layout = getLayoutByContext(context);

    // Watch para campos condicionais
    const isPCD = watch('isPCD');
    const pcdTypes = watch('pcdTypes') || [];
    const watchedData = watch();

    // Verificar se o fieldset deve ser exibido
    if (!shouldShow('isPCD')) {
        return null;
    }

    return (
        <fieldset className={layout.fieldsetStyle}>
            <legend className={layout.legendStyle}>Necessidades Especiais</legend>
            <div className="space-y-4 pt-2">
                {/* Checkbox principal */}
                <div className="flex items-center">
                    <Controller
                        name="isPCD"
                        control={control}
                        render={({ field, fieldState }) => (
                            <>
                                <input
                                    type="checkbox"
                                    id="isPCD"
                                    checked={field.value || false}
                                    onChange={(e) => field.onChange(e.target.checked)}
                                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                    disabled={isSubmitting}
                                />
                                {fieldState.error && (
                                    <p className="ml-2 text-sm text-red-600" role="alert">
                                        {fieldState.error.message}
                                    </p>
                                )}
                            </>
                        )}
                    />
                    <label htmlFor="isPCD" className="ml-2 block text-sm text-gray-900">
                        É Pessoa com Deficiência (PCD)
                    </label>
                </div>

                {/* Tipos de deficiência (condicional) */}
                {isPCD && shouldShow('pcdTypes') && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            Tipos de Deficiência
                        </label>
                        <Controller
                            name="pcdTypes"
                            control={control}
                            render={({ field, fieldState }) => (
                                <>
                                    <div className="flex flex-wrap gap-4">
                                        {pcdOptions.map(option => (
                                            <div key={option} className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    id={`pcd-${option}`}
                                                    checked={(field.value || []).includes(option)}
                                                    onChange={(e) => {
                                                        const currentTypes = field.value || [];
                                                        if (e.target.checked) {
                                                            field.onChange([...currentTypes, option]);
                                                        } else {
                                                            field.onChange(currentTypes.filter((type: string) => type !== option));
                                                        }
                                                    }}
                                                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                                    disabled={isSubmitting}
                                                />
                                                <label htmlFor={`pcd-${option}`} className="ml-2 text-sm text-gray-700">
                                                    {option}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                    {fieldState.error && (
                                        <p className="mt-1 text-sm text-red-600" role="alert">
                                            {fieldState.error.message}
                                        </p>
                                    )}
                                </>
                            )}
                        />
                    </div>
                )}

                {/* Documento PCD (condicional) */}
                {isPCD && shouldShow('pcdDocumentFile') && (
                    <div>
                        <Controller
                            name="pcdDocumentFile"
                            control={control}
                            render={({ field, fieldState }) => (
                                <FileUploadInput
                                    name="pcdDocumentFile"
                                    label="Laudo Médico PCD"
                                    fileUrl={watchedData.pcdDocumentUrl}
                                    fileName={watchedData.pcdDocumentFile?.name}
                                    isSubmitting={isSubmitting}
                                    isRequired={false}
                                    onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;

                                        field.onChange(file);
                                        await handleFileChange(e, {
                                            fieldName: 'pcdDocumentFile',
                                            urlFieldName: 'pcdDocumentUrl',
                                            pathPrefix: 'pcd_documents',
                                            id: collaboratorId
                                        });
                                    }}
                                    progress={uploadStates['pcdDocumentFile']?.progress}
                                    status={uploadStates['pcdDocumentFile']?.status || 'idle'}
                                    error={fieldState.error?.message}
                                />
                            )}
                        />
                    </div>
                )}
            </div>
        </fieldset>
    );
};

export default SpecialNeedsFieldset; 